import React, { useState, useEffect } from 'react';
import { Mail, Send, MessageSquare, Building2 } from 'lucide-react';
import usePageTitle from '../hooks/usePageTitle';
import '../css/Contact.css';
import api from '../helpers/api';
import useRecaptchaV3 from '../hooks/reCaptchaV3';

const Contact = () => {
  usePageTitle('Contact');

  const executeRecaptcha = useRecaptchaV3('6Ldyg9AqAAAAAIwizLYlNWcB7-GxLLdDtK6DlODk');

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: 'Question générale',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  // Clean up any state updates when component unmounts
  useEffect(() => {
    // Show reCAPTCHA on mount
    const showRecaptcha = () => {
      const recaptchaElements = document.getElementsByClassName('grecaptcha-badge');
      if (recaptchaElements.length > 0) {
        Array.from(recaptchaElements).forEach(element => {
          element.style.visibility = 'visible';
          element.style.display = 'block';
        });
      }
    };

    // Call show function after a short delay to ensure reCAPTCHA has loaded
    const timeoutId = setTimeout(showRecaptcha, 1000);

    // Cleanup function
    return () => {
      clearTimeout(timeoutId);
      const recaptchaElements = document.getElementsByClassName('grecaptcha-badge');
      if (recaptchaElements.length > 0) {
        Array.from(recaptchaElements).forEach(element => {
          element.style.visibility = 'hidden';
          element.style.display = 'none';
        });
      }
      setIsSubmitting(false);
      setSubmitStatus(null);
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const email = e.target.email.value;
    if (email.includes('bot') || email.endsWith('.ru')) return;

    setIsSubmitting(true);

    try {
      const recaptchaToken = await executeRecaptcha('contact');
      await api.post('/contacts', {data: {token: recaptchaToken, ...formData}});
      // await api.post('/contacts', {data: formData});
      
      setFormData({
        name: '',
        email: '',
        subject: 'Question générale',
        message: ''
      });
      setSubmitStatus('success');
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="contact-page">
      <div className="contact-hero">
        <div className="container">
          <h1>Contactez-nous</h1>
          <p>Une question ? Une suggestion ? N'hésitez pas à nous contacter !</p>
        </div>
      </div>

      <div className="container">
        <div className="contact-grid">
          <div className="contact-form-container">
            <div className="form-card">
              <h2>Envoyez-nous un message</h2>
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label htmlFor="name">Nom complet</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Votre nom"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email">Email</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="votre@email.com"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="subject">Sujet</label>
                  <select
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                  >
                    <option value="Question générale">Question générale</option>
                    <option value="Proposition commerciale">Proposition commerciale</option>
                    <option value="Suggestion d'établissement">Suggestion d'établissement</option>
                    <option value="Signaler un problème">Signaler un problème</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="message">Message</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    placeholder="Votre message..."
                    rows="5"
                  ></textarea>
                </div>

                {submitStatus === 'success' && (
                  <div className="success-message">
                    Message envoyé avec succès ! Nous vous répondrons dans les plus brefs délais.
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="error-message">
                    Une erreur s'est produite. Veuillez réessayer.
                  </div>
                )}

                <button
                  type="submit"
                  className="submit-button"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    'Envoi en cours...'
                  ) : (
                    <>
                      <Send size={20} />
                      Envoyer le message
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>

          <div className="contact-info">
            <div className="info-card">
              <h3>Informations de contact</h3>
              <div className="info-items">
                <div className="info-item">
                  <Mail className="info-icon" />
                  <div>
                    <h4>Email</h4>
                    <p>support&#64;tispot971&#46;com</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="info-card">
              <h3>Vous êtes un professionnel ?</h3>
              <p>Vous souhaitez ajouter ou mettre à jour votre établissement sur Ti'Spot 971 ?</p>
              <div className="business-contacts">
                <div className="business-contact-item">
                  <MessageSquare className="info-icon" />
                  <p>Utilisez le formulaire en sélectionnant "Proposition commerciale"</p>
                </div>
                <div className="business-contact-item">
                  <Building2 className="info-icon" />
                  <p>Notre équipe commerciale vous recontactera sous 24-48h</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;